import { useEffect, useRef, useState, useCallback } from "react";
import { wsUrl } from "@/lib/api";

// Reliable WebSocket with auto-reconnect (built for weak networks)
export function useSocket(onMessage) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const aliveRef = useRef(true);
  const handlerRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);
  handlerRef.current = onMessage;

  const connect = useCallback(() => {
    const token = localStorage.getItem("rc_token");
    if (!token) return;
    let ws;
    try { ws = new WebSocket(wsUrl(token)); } catch (e) { return; }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      retryRef.current = 0;
    };
    ws.onmessage = (ev) => {
      try { handlerRef.current && handlerRef.current(JSON.parse(ev.data)); } catch (e) {}
    };
    ws.onclose = () => {
      setConnected(false);
      if (!aliveRef.current) return;
      const delay = Math.min(1000 * 2 ** retryRef.current, 15000);
      retryRef.current += 1;
      setTimeout(() => aliveRef.current && connect(), delay);
    };
    ws.onerror = () => { try { ws.close(); } catch (e) {} };
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    connect();
    const ping = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 25000);
    return () => {
      aliveRef.current = false;
      clearInterval(ping);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return { connected, send };
}
